"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.InputChip = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _ChipLabel = require("../AssistChip/ChipLabel");
var _TagCloseButton = require("../TagCloseButton/TagCloseButton");
var _InputChip = require("./InputChip.styles");
var _excluded = ["appearance", "avatar", "children", "chipElevation", "closeButtonVoiceOver", "defaultOpen", "disabled", "icon", "isOpen", "onClose", "size", "renderCloseButton", "textTransform"];
/**
 * Input chips are used to hold text that has been entered or selected
 * by the user (ex: multiple Combobox selections, addresses in an email's "to:" field).
 * When text is converted into an input chip, it helps confirm a user's input.
 *
 * @related ['AssistChip', 'Combobox', 'FilterChip', 'Tag']
 * @since 8.97.0
 */
var InputChip = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var appearance = props.appearance,
    avatar = props.avatar,
    children = props.children,
    chipElevation = props.chipElevation,
    closeButtonVoiceOver = props.closeButtonVoiceOver,
    defaultOpen = props.defaultOpen,
    disabled = props.disabled,
    icon = props.icon,
    controlledOpen = props.isOpen,
    onClose = props.onClose,
    size = props.size,
    renderCloseButton = props.renderCloseButton,
    textTransform = props.textTransform,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  var _useState = (0, _react.useState)(defaultOpen),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    uncontrolledOpen = _useState2[0],
    setUncontrolledOpen = _useState2[1];
  var isOpen = uncontrolledOpen;
  if (typeof controlledOpen !== 'undefined') {
    isOpen = controlledOpen;
  }
  var closeCallback = (0, _react.useCallback)(function () {
    setUncontrolledOpen(false);
    if (typeof onClose === 'function') {
      onClose(props);
    }
  }, [onClose, props, setUncontrolledOpen]);
  if (typeof isOpen === 'boolean' && !isOpen) {
    return null;
  }
  var adjustedAvatar;
  if (avatar) {
    // Prevent img alt text from being read and
    // chip from being announced as a "button, group"
    adjustedAvatar = /*#__PURE__*/(0, _react.cloneElement)(avatar, {
      'aria-hidden': 'true',
      'as': 'span'
    });
  }
  var closeButton;
  if (isOpen && typeof renderCloseButton === 'function') {
    closeButton = renderCloseButton({
      'aria-label': closeButtonVoiceOver || "Remove " + children,
      'aria-disabled': disabled,
      disabled: disabled,
      'onClick': closeCallback
    });
  }
  var chip = /*#__PURE__*/_react.default.createElement(_InputChip.StyledInputChip, (0, _extends2.default)({
    appearance: "gray",
    chipElevation: chipElevation,
    disabled: disabled,
    ref: forwardedRef,
    size: "md",
    textTransform: "none"
  }, rest), avatar ? adjustedAvatar : null, icon, /*#__PURE__*/_react.default.createElement(_ChipLabel.ChipLabel, null, children), closeButton);
  return chip;
});
exports.InputChip = InputChip;
InputChip.displayName = 'InputChip';
InputChip.propTypes = {
  /**
   * Adds a leading avatar. Takes an `Avatar` component. Ignores `Avatar`’s `size` prop
   * (i.e. the avatar size is fixed).
   */
  avatar: _propTypes.default.node,
  /**
   * The input chip’s text label. To add decorators, use the `icon` and `avatar` props.
   */
  children: _propTypes.default.node.isRequired,
  /**
   * When true, adds a drop shadow. Useful for making the input chip easier to see on
   * certain backgrounds (ex: on top of a photo or map)
   */
  chipElevation: _propTypes.default.bool,
  /**
   * When enabled, the input chip will close in an uncontrolled manner.
   */
  defaultOpen: _propTypes.default.bool,
  /**
   * Sets the input chip as disabled.
   */
  disabled: _propTypes.default.bool,
  /**
   * Adds a leading icon. Takes an `Icon` component but ignores its `size` prop
   * (i.e. the icon size is fixed). Does not support `DetailedIcon`.
   */
  icon: _propTypes.default.node,
  /**
   * When enabled, the input chip will close in a controlled manner.
   */
  isOpen: _propTypes.default.bool,
  /**
   * The text that will be announced to screen readers when you focus on the
   * chip's close button. Used as the close button's `aria-label` value.
   *
   * Defaults to "Remove {chip label}"
   */
  closeButtonVoiceOver: _propTypes.default.string,
  /**
   * Function called when the chip is closed. The chip’s props will be passed as
   * the only argument.
   */
  onClose: _propTypes.default.func,
  /**
   * A render function that returns a close button.
   */
  renderCloseButton: _propTypes.default.func
};
InputChip.defaultProps = {
  avatar: null,
  chipElevation: false,
  disabled: false,
  icon: null,
  renderCloseButton: function renderCloseButton(props) {
    return /*#__PURE__*/_react.default.createElement(_TagCloseButton.TagCloseButton, props);
  }
};